/**
 * Heritage Dahlias, Admin password reset tool.
 *
 * Lets the admin reset any account's password across all four portals
 * (admin / management / Heritage HQ / Bloom Club grower) in a single click.
 * The new password is applied to whichever collection holds that email.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "./api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { KeyRound, Shuffle, Copy } from "lucide-react";

function generateTempPassword() {
  // Friendly, easy-to-read temporary password
  const adj = ["Sunny", "Bright", "Sage", "Berry", "Plum", "Rose", "Coral", "Cream"];
  const noun = ["Dahlia", "Petal", "Bloom", "Garden", "Tuber", "Field", "Vase"];
  const n = Math.floor(100 + Math.random() * 900);
  return `${adj[Math.floor(Math.random()*adj.length)]}${noun[Math.floor(Math.random()*noun.length)]}${n}!`;
}

export default function ConsoleResetPassword() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || pw.length < 8) {
      toast.error("Enter an email and a password of 8+ characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/auth/reset-password", {
        email: email.trim(),
        new_password: pw,
      });
      setLast({ email: email.trim(), pw, portals: r.data.updated_in });
      toast.success(`Password reset across ${r.data.updated_in.join(", ")}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset password.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied."); }
    catch (e) { window.prompt("Copy:", txt); }
  };

  return (
    <div data-testid="admin-reset-password" className="space-y-6 max-w-2xl">
      <div>
        <span className="font-label tracking-[0.4em] text-[11px] text-[#6E1E3A]">
          ✦ PASSWORD RESET ✦
        </span>
        <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] mt-2 leading-[1.05]">
          Reset a <span className="font-display-italic text-[#6E1E3A]">password.</span>
        </h1>
        <p className="mt-3 text-sm text-[var(--dahlias-ink)]/70">
          Sets a new password for any account across all four portals (Admin, Management, Heritage HQ, Bloom Club Grower). Use this when a customer or staff member forgets theirs.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/25 wobble-a p-5 md:p-6 space-y-4"
      >
        <div>
          <Label className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">Email address</Label>
          <Input
            type="email"
            data-testid="reset-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="customer@example.com"
            className="mt-1.5 h-11 wobble-pill"
            required
          />
        </div>
        <div>
          <Label className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">New password</Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              data-testid="reset-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Min 8 characters"
              className="h-11 wobble-pill font-mono"
              required
            />
            <button
              type="button"
              data-testid="reset-generate"
              onClick={() => setPw(generateTempPassword())}
              className="h-11 px-4 wobble-pill bg-[var(--dahlias-cream)] hover:bg-[var(--dahlias-sage)]/15 border border-[var(--dahlias-sage)]/30 text-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <Shuffle className="w-4 h-4" /> Generate
            </button>
          </div>
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="reset-submit"
          className="h-11 px-6 bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] wobble-pill inline-flex items-center gap-2"
        >
          <KeyRound className="w-4 h-4" /> {busy ? "Resetting…" : "Reset password"}
        </Button>
      </form>

      {last && (
        <div
          data-testid="reset-success"
          className="bg-[#FBE6D8] border-2 border-[#6E1E3A]/40 wobble-a p-5 space-y-3"
        >
          <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Done ✓</h2>
          <p className="text-sm text-[var(--dahlias-ink)]/80">
            Updated in: <strong>{(last.portals || []).join(", ")}</strong>. Share these details with the customer (we don't email them automatically yet).
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-sm">
            <dt className="text-[var(--dahlias-ink)]/60 font-label uppercase tracking-[0.2em] text-[10px]">Email</dt>
            <dd className="flex items-center gap-2"><code className="font-mono text-[#6E1E3A]">{last.email}</code><button onClick={() => copy(last.email)} className="text-xs text-[#6E1E3A] hover:underline"><Copy className="w-3 h-3 inline" /> copy</button></dd>
            <dt className="text-[var(--dahlias-ink)]/60 font-label uppercase tracking-[0.2em] text-[10px]">Password</dt>
            <dd className="flex items-center gap-2"><code className="font-mono text-[#6E1E3A] font-bold">{last.pw}</code><button onClick={() => copy(last.pw)} className="text-xs text-[#6E1E3A] hover:underline"><Copy className="w-3 h-3 inline" /> copy</button></dd>
          </dl>
        </div>
      )}
    </div>
  );
}
